import { useEffect, useState } from 'react';
import InstanceApi from '../api/instance/InstanceApi';
import Status from '../types/Status';
import InstanceTarget from '../types/instance/InstanceTarget';
import { TARGET_TYPE_DATABASE } from '../component/Constants';
import { showError } from '../utils/showAlert';

/**
 * Configuration constants for the instance status polling system
 */
const CACHE_DURATION = 4000; // 4 seconds cache duration
const POLLING_INTERVAL = 5000; // 5 seconds polling interval

/**
 * Default initial status for instances before data is loaded
 */
const initialStatus: Status = {
    type: TARGET_TYPE_DATABASE,
    sensorstatus: 200,
    message: 'OK',
    reason: '',
    updated: null,
    timedout: false,
    state: 'Started',
    status: 200
};

export function buildInstanceStatus(instance: Partial<InstanceTarget>): Status {
    return {
        type: TARGET_TYPE_DATABASE,
        sensorstatus: instance.sensorstatus ?? 200,
        message: instance.message ?? 'OK',
        reason: instance.reason ?? '',
        updated: instance.updated ?? null,
        timedout: instance.timedout ?? false,
        state: instance.state ?? 'Started',
        status: instance.status ?? 200
    };
}

/**
 * Global cache for all instance statuses to minimize API calls across components
 */
interface InstanceStatusCache {
    data: Map<number, Status>;
    timestamp: number;
    isLoading: boolean;
    subscribers: Set<(statuses: Map<number, Status>) => void>;
}

const globalInstanceStatusCache: InstanceStatusCache = {
    data: new Map(),
    timestamp: 0,
    isLoading: false,
    subscribers: new Set()
};

let globalPollingTimer: NodeJS.Timeout | null = null;

/**
 * Fetches all instance statuses from the API in a single call
 * @returns Promise that resolves to a Map of instance IDs to their status objects
 * @throws Error if the API call fails
 */
async function fetchAllInstanceStatuses(): Promise<Map<number, Status>> {
    try {
        const response = await InstanceApi.getInstances();
        const statusMap = new Map<number, Status>();
        
        if (response && Array.isArray(response)) {
            response.forEach((instance: InstanceTarget) => {
                if (instance?.id) {
                    statusMap.set(instance.id, buildInstanceStatus(instance));
                }
            });
        }
        
        return statusMap;
    } catch (error: any) {
        console.error('Failed to fetch instance statuses:', error);
        showError('Failed to fetch instance statuses', error.message || error.toString());
        return new Map<number, Status>();
    }
}

/**
 * Polls all instance statuses from the API and notifies all subscribers
 * Implements safeguards to prevent concurrent requests
 */
async function pollAllInstanceStatuses(): Promise<void> {
    if (globalInstanceStatusCache.isLoading) {
        return; // Prevent concurrent requests
    }

    try {
        globalInstanceStatusCache.isLoading = true;
        const allStatuses = await fetchAllInstanceStatuses();
        
        // Update global cache
        globalInstanceStatusCache.data = allStatuses;
        globalInstanceStatusCache.timestamp = Date.now();
        
        // Notify all subscribers with the updated data
        globalInstanceStatusCache.subscribers.forEach(callback => {
            try {
                callback(allStatuses);
            } catch (callbackError) {
                console.error('Error in instance status subscriber callback:', callbackError);
            }
        });
    } catch (error) {
        console.error('Failed to fetch instance statuses:', error);
    } finally {
        globalInstanceStatusCache.isLoading = false;
    }
}

/**
 * Starts the global polling timer if not already running
 * Only one polling timer will run regardless of how many components use the hook
 */
function startGlobalPolling(): void {
    if (globalPollingTimer) {
        return; // Already polling
    }

    const poll = (): void => {
        pollAllInstanceStatuses();
        globalPollingTimer = setTimeout(poll, POLLING_INTERVAL);
    };

    poll(); // Start immediately
}

/**
 * Stops the global polling timer if there are no more subscribers
 * Helps prevent unnecessary API calls when no components need instance status data
 */
function stopGlobalPollingIfNoSubscribers(): void {
    if (globalInstanceStatusCache.subscribers.size === 0 && globalPollingTimer) {
        clearTimeout(globalPollingTimer);
        globalPollingTimer = null;
    }
}

/**
 * React hook for fetching and monitoring instance status with optimized global polling
 * 
 * Features:
 * - Global cache to prevent duplicate API calls across multiple components
 * - Automatic polling with configurable intervals
 * - Real-time updates for all subscribers when data changes
 * - Cleanup when components unmount to prevent memory leaks
 * 
 * @param instanceId - The ID of the instance to monitor (must be > 0)
 * @returns Status object for the specified instance
 */
export function useInstanceStatus(instanceId: number): Status {
    const [status, setStatus] = useState<Status>(initialStatus);

    useEffect(() => {
        // Skip polling for invalid instance IDs
        if (!instanceId || instanceId <= 0) {
            return;
        }

        // Callback function to update this component's status when global data changes
        const handleGlobalUpdate = (allStatuses: Map<number, Status>): void => {
            const instanceStatus = allStatuses.get(instanceId);
            if (instanceStatus) {
                setStatus(instanceStatus);
            }
        };

        // Subscribe to global status updates
        globalInstanceStatusCache.subscribers.add(handleGlobalUpdate);

        // Use cached data if it's still fresh
        const now = Date.now();
        const isCacheValid = now - globalInstanceStatusCache.timestamp < CACHE_DURATION;
        if (isCacheValid) {
            const cachedStatus = globalInstanceStatusCache.data.get(instanceId);
            if (cachedStatus) {
                setStatus(cachedStatus);
            }
        }

        // Start global polling (only starts if not already running)
        startGlobalPolling();

        // Cleanup function runs when component unmounts or instanceId changes
        return () => {
            globalInstanceStatusCache.subscribers.delete(handleGlobalUpdate);
            stopGlobalPollingIfNoSubscribers();
        };
    }, [instanceId]);

    return status;
}

export function useAllInstanceStatuses(): Map<number, Status> {
    const [statuses, setStatuses] = useState<Map<number, Status>>(new Map(globalInstanceStatusCache.data));

    useEffect(() => {
        const handleGlobalUpdate = (allStatuses: Map<number, Status>): void => {
            setStatuses(new Map(allStatuses));
        };

        globalInstanceStatusCache.subscribers.add(handleGlobalUpdate);

        const now = Date.now();
        const isCacheValid = now - globalInstanceStatusCache.timestamp < CACHE_DURATION;
        if (isCacheValid) {
            setStatuses(new Map(globalInstanceStatusCache.data));
        }

        startGlobalPolling();

        return () => {
            globalInstanceStatusCache.subscribers.delete(handleGlobalUpdate);
            stopGlobalPollingIfNoSubscribers();
        };
    }, []);

    return statuses;
}
